import { useState, useEffect, useCallback, useMemo } from "react";
import { COLOR_CODES } from "../../../common";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { BrushStart, LegendKey } from "../../../models/chart_model";
import { RajasthanDiscomDrawal } from "../../../models/rajasthan";
import { getRajasthanDiscomDrawlData } from "../Utility/RestApi";
import {
  formatDateForAPI,
  getLast7DaysDateRange,
  R_CHART_TYPES,
} from "../const";
import { GnaRuvnlFooter } from "../components";
import { DynamicDatePicker } from "../../../components/DatePicker";
import { convertToString } from "../../../extensions/date";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { FilterTab } from "../../../components/Filters";
import { DataTabs, DEMAND_UNITS } from "./Data";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector, useDispatch } from "react-redux";
import { ExchangeColors } from "../Exchange3/FormatData";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { RajasthanDrawalPeakAnalysis } from "./DrawalPeak";
import { RajasthanDrawalSummaryAnalysis } from "./DrawalSummary";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";

// ▶ Import the shared hook
import { useLegendFilter } from "../../../hooks/useLegendFilter";

// ─── constants ───────────────────────────────────────────────────
const GROUP_BY = [
  { label: "Area", key: "area", values: ["area", "discom"] },
  { label: "Discom", key: "discom", values: ["discom"] },
  { label: "District", key: "district", values: ["district", "district_only"] },
];

const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL", apiKey: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL", apiKey: "JVVNL" },
  { label: "AVVNL", key: "AVVNL", apiKey: "AVVNL" },
];

// Helper function to convert API keys to display names
function getDisplayDiscomName(apiKey: string): string {
  const discom = DISCOM.find((d) => d.apiKey === apiKey);
  return discom ? discom.label : apiKey;
}

let X_AXIS = [
  { label: "Time Slot", key: "time", values: ["block", "date"], isTime: true },
  {
    label: "Discom/ Area",
    key: "station",
    isTime: false,
    values: ["area", "discom"],
  },
];

let Intervals = [
  { label: "Time Slot", key: "time", values: ["block", "date"], isTime: true },
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = DEMAND_UNITS;

// ─── component ────────────────────────────────────────────────────────────────
export function RajasthanDrawal() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const dispatch = useDispatch();

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<RajasthanDiscomDrawal[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<string[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([GROUP_BY[1]]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  const [selectedDistricts, setSelectedDistricts] = useState<any[]>([]);

  const formatRegionLabel = useCallback(
    (value: string | undefined, discom: string | undefined) => {
      const cleanValue = (value ?? "").toString().trim();
      const cleanDiscom = (discom ?? "").toString().trim();
      if (!cleanDiscom && !cleanValue) return "Unknown";
      if (!cleanDiscom) return cleanValue;
      if (!cleanValue) return cleanDiscom;
      return `${cleanValue} (${cleanDiscom})`;
    },
    [],
  );

  // ─── fetch data ──────────────────────────────────────────────────
  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
    const dates = DefaultDates[activeTab].defaultRangeDate;
    setLoading(true);
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);
    const newData = await getRajasthanDiscomDrawlData({
      startDate: startDateStr,
      endDate: endDateStr,
    });
    setData(newData);
    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item: any) => item.date)
            .sort(
              (a: any, b: any) => new Date(a).getTime() - new Date(b).getTime(),
            ),
        ),
      ),
    );
    setUniqueBlock(Array.from(new Set(newData.map((item: any) => item.block))));
    setLoading(false);
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  // ─── helpers ────────────────────────────────────────────────
  function _handleGroupByChange(index: number) {
    const newGroup = GROUP_BY[index];
    setSelectedGroupKeys([newGroup]);
    setSelectedDistricts([]);
    if (newGroup.key !== "station") setSelectedArea([]);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  const keys = useCallback(() => {
    return selectedBlock.length > 0
      ? ["date", "block", "month"]
      : selectedInterval.values;
  }, [selectedBlock.length, selectedInterval.values]);

  // ─── data processing ─────────────────────────────────────────
  // ─── data processing (Optimized with useMemo) ─────────────────────────
  const processedData = useMemo(() => {
    return data.map((item) => {
      const date = new Date(item.date);
      const shortMonth = date.toLocaleDateString("en-US", { month: "short" });
      const shortYear = date.getFullYear().toString().slice(-2);

      const area = (item.area ?? "").toString().trim();
      const district = (item.district ?? "").toString().trim();
      const discom = (item.discom ?? "").toString().trim();
      const station = formatRegionLabel(area, discom);

      return {
        ...item,
        area,
        district,
        discom,
        station,
        mwh: item.mw / 4,
        lu: item.mw / 400,
        month: `${shortMonth}-${shortYear}`,
      };
    });
  }, [data]);

  const discomFilteredData = useMemo(() => {
    if (selectedDiscom.length === 0) return processedData;
    return processedData.filter((item) => selectedDiscom.includes(item.discom));
  }, [processedData, selectedDiscom]);

  const uniqueDistricts = useMemo(() => {
    const labels = Array.from(
      new Set(
        discomFilteredData.map((item) =>
          formatRegionLabel(item.district, item.discom),
        ),
      ),
    );

    return labels.filter((label) => {
      const trimmed = label.trim();
      return (
        trimmed !== "" &&
        !trimmed.startsWith("(") &&
        !trimmed.toLowerCase().includes("undefined")
      );
    });
  }, [discomFilteredData, formatRegionLabel]);

  const districtFilteredData = useMemo(() => {
    if (selectedDistricts.length === 0) return discomFilteredData;
    const distStrings = selectedDistricts.map((d: any) => d.value);
    return discomFilteredData.filter((item) =>
      distStrings.includes(formatRegionLabel(item.district, item.discom)),
    );
  }, [discomFilteredData, selectedDistricts, formatRegionLabel]);

  const uniqueArea = useMemo(() => {
    const labels = Array.from(
      new Set(
        districtFilteredData.map((item) =>
          formatRegionLabel(item.area, item.discom),
        ),
      ),
    );

    return labels.filter((label) => {
      const trimmed = label.trim();
      return (
        trimmed !== "" &&
        !trimmed.startsWith("(") &&
        !trimmed.toLowerCase().includes("undefined")
      );
    });
  }, [districtFilteredData, formatRegionLabel]);

  const finalFilteredData = useMemo(() => {
    let res = districtFilteredData;
    if (selectedArea.length > 0) {
      const areaStrings = selectedArea.map((a: any) => a.value);
      res = res.filter((item) =>
        areaStrings.includes(formatRegionLabel(item.area, item.discom)),
      );
    }
    if (selectedDate.length > 0) {
      const dateStrings = selectedDate.map((opt) => opt.value);
      res = res.filter((item) => dateStrings.includes(item.date));
    }
    if (selectedBlock.length > 0) {
      const blockValues = selectedBlock.map((opt) => opt.value);
      res = res.filter((item) => blockValues.includes(item.block));
    }
    return res;
  }, [districtFilteredData, selectedArea, selectedDate, selectedBlock]);

  const { chartData, TableData, chartKeys } = useMemo(() => {
    if (data.length === 0)
      return { chartData: [], TableData: [], chartKeys: [] };

    const determineGroupBy = () => {
      if (selectedXaxis.isTime) {
        if (selectedArea.length > 0) return ["station"];
        if (selectedDistricts.length > 0) return ["district"];
        return selectedGroupKeys.map((k) => k.key);
      }
      return [];
    };

    const par: ChartDataParams = {
      keys: keys(),
      tableKeys: ["date", "block", "month"],
      groupBy: determineGroupBy(),
      volumeKey: "",
      aggregationKeys: ["lu", "mwh", "mw"],
      averageKeys: ["mw"],
      priceKey: "",
      averageDuplicationKeys: ["block", "date"],
      data: finalFilteredData,
    };

    const cData = FormatChartData(par);

    const sortedChartData = (cData.data || []).sort((a, b) => {
      if (a.date && b.date) {
        const dateComparison =
          new Date(a.date).getTime() - new Date(b.date).getTime();
        if (dateComparison !== 0) return dateComparison;
      }
      if (a.block && b.block) return Number(a.block) - Number(b.block);
      return 0;
    });

    return {
      chartData: sortedChartData,
      TableData: cData.tableData || [],
      chartKeys: cData.keys,
    };
  }, [
    data.length,
    finalFilteredData,
    selectedGroupKeys,
    selectedXaxis,
    selectedInterval,
    selectedArea.length,
    selectedDistricts.length,
    keys,
  ]);

  const districtOptions = useMemo(() => {
    return uniqueDistricts.map((item: any) => ({
      value: item,
      label: item,
    }));
  }, [uniqueDistricts]);

  const areaOptions = useMemo(() => {
    return uniqueArea.map((item) => ({
      value: item,
      label: item,
    }));
  }, [uniqueArea]);

  // ─── FIXED: Create legends matching the child component pattern ─────────────────────
  const legends = useMemo(() => {
    if (!chartKeys?.length) return [];

    return chartKeys.map((key) => {
      let displayKey = key;

      if (selectedGroupKeys[0]?.key === "station") {
        if (key.includes("_")) {
          const parts = key.split("_");
          const discomKey = parts[parts.length - 1];
          const areaKey = parts.slice(0, -1).join("_");

          const discomDisplayName = getDisplayDiscomName(discomKey);
          const cleanAreaKey = areaKey?.trim() ?? "";

          if (!cleanAreaKey || cleanAreaKey.toLowerCase() === "undefined") {
            displayKey = discomDisplayName;
          } else if (cleanAreaKey.toLowerCase() === "rajasthan") {
            displayKey = discomDisplayName;
          } else {
            displayKey = `${cleanAreaKey} (${discomDisplayName})`;
          }
        } else {
          displayKey = getDisplayDiscomName(key);
        }
      } else if (selectedGroupKeys[0]?.key === "discom") {
        displayKey = getDisplayDiscomName(key);
      } else if (selectedGroupKeys[0]?.key === "district") {
        if (key.includes("_")) {
          const parts = key.split("_");
          const discomKey = parts[parts.length - 1];
          const districtKey = parts.slice(0, -1).join("_");

          const discomDisplayName = getDisplayDiscomName(discomKey);
          const cleanDistrictKey = districtKey?.trim() ?? "";

          if (
            !cleanDistrictKey ||
            cleanDistrictKey.toLowerCase() === "undefined"
          ) {
            displayKey = discomDisplayName;
          } else {
            displayKey = `${cleanDistrictKey} (${discomDisplayName})`;
          }
        } else {
          displayKey = getDisplayDiscomName(key);
        }
      }

      // If not time-based, use generic label
      if (!selectedXaxis.isTime) {
        displayKey = "Volume Drawal";
      }

      return {
        dataKey: `${key}_${selectedUnit.toLowerCase()}`,
        name: displayKey,
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined
            ? selectedChartType.stackId
            : undefined,
      };
    });
  }, [
    chartKeys,
    selectedUnit,
    selectedXaxis.isTime,
    selectedGroupKeys,
    selectedChartType,
  ]);

  // ─── summary extra keys / labels ───────────────────────────
  function summaryExtraKeys() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return selectedInterval.key === "month" ? ["month"] : ["date", "block"];
      }
      return ["date", "block"];
    }
    return [selectedInterval.values[0]];
  }

  function summaryExtraLabels() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return selectedInterval.key === "month" ? ["Month"] : ["Date", "Block"];
      }
      return ["Date", "Block"];
    }
    return [selectedInterval.values[0]];
  }

  // ▶ CRITICAL FIX: Don't pass chartData as resetDep - it causes reset on tab switch!
  // Only pass the legends array so it resets when legends actually change (filter changes)
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);
  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      return "block";
    }
    return selectedInterval.key;
  }, [selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    if (selectedInterval.isTime && selectedBlock.length > 0) {
      return "date";
    }
    if (selectedInterval.isTime) {
      return "date";
    }
    return selectedInterval.values[1];
  }

  // ─── sub-page routes ────────────────────────────────────────
  if (selectedXAxisMode.value === "peak") {
    return (
      <RajasthanDrawalPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedArea={selectedArea}
        onAreaChange={setSelectedArea}
        selectedDistricts={selectedDistricts}
        onDistrictsChange={setSelectedDistricts}
      />
    );
  }

  if (selectedXAxisMode.value === "summary") {
    return (
      <RajasthanDrawalSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
        selectedArea={selectedArea}
        onAreaChange={setSelectedArea}
        selectedDistricts={selectedDistricts}
        onDistrictsChange={setSelectedDistricts}
      />
    );
  }

  // ─── render ─────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          <FilterMenu
            title="Interval"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => setSelectedInterval(Intervals[index])}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => handleUnitChange(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={setSelectedDiscom}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            onSelect={(item, index) => _handleGroupByChange(index)}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(R_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading Restricted Demand" subtitle="Fetching Chhattisgarh restricted demand data…" /> : (<>
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">
              Restricted Demand ({selectedUnit})
            </h2>
            <div className="flex gap-x-6 justify-end">
              {selectedGroupKeys[0]?.key === "district" && (
                <div className="min-w-60">
                  <SearchBox
                    options={districtOptions}
                    isMany={true}
                    placeholder="Search District"
                    selectedOptions={selectedDistricts}
                    onChange={(v: any) => {
                      setSelectedDistricts(v);
                      setSelectedArea([]);
                    }}
                  />
                </div>
              )}
              {(selectedGroupKeys[0]?.key === "area" ||
                (selectedGroupKeys[0]?.key === "district" &&
                  selectedDistricts.length > 0)) && (
                <div className="min-w-60">
                  <SearchBox
                    options={areaOptions}
                    isMany={true}
                    placeholder="Search Area"
                    selectedOptions={selectedArea}
                    onChange={(v: any) => setSelectedArea(v)}
                  />
                </div>
              )}
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {/* ▶ CHART - matching child component pattern */}
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedInterval.isTime}
              legends={legends}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
              onlyTitle={selectedBlock.length > 0 && selectedInterval.isTime}
              barCategoryGap={selectedXaxis.isTime ? 2 : 40}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              xDataKey={xDataKey}
              secondXDataKey={secondDataKey()}
            />
          )}

          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </>)}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>GSS Report (RUVITL)</p>}
              text="1. Sum of all GSS load = Actual drawal (Restricted demand)&#10;2. MW = Represents the average (MW) when the selected time interval is Date or Month."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
export default RajasthanDrawal;
